import { ctx, hasRR, W } from '../canvas.js';
import { state } from '../state.js';
import { BUBBLE_GAP, BUBBLE_PAD, BUBBLE_POP_MS, UI_FONT } from '../config.js';
import { clamp } from '../utils.js';
import { bubbleAge } from '../banter.js';
import { gorillaHead } from './gorilla.js';

const FONT_SIZE = 11;
const LINE_H = 15;
/** How far a bubble slides up as it pops in. */
const RISE = 6;
/** Along the bottom of its life it fades over this last share of it. */
const FADE_FROM = 0.75;

/**
 * Speech bubbles: an arcade card with a tail, over the head of whoever is
 * talking. Drawn after everything else, so a line is never hidden by a hat, a
 * hand or a mushroom cloud — if a gorilla has gone to the trouble of saying
 * something, it is getting read.
 */
export function drawBubbles() {
  if (!state.bubbles || !state.bubbles.length) return;

  ctx.save();
  ctx.font = `700 ${FONT_SIZE}px ${UI_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const b of state.bubbles) {
    const p = state.players[b.idx];
    if (!p || !p.alive) continue;

    const head = gorillaHead(p, b.idx);
    const w = Math.ceil(ctx.measureText(b.text).width) + BUBBLE_PAD * 2;
    const h = LINE_H + BUBBLE_PAD * 2;

    // Clamped to the canvas, so a gorilla hugging an edge does not have its line
    // cut in half by the frame.
    const x = Math.round(clamp(head.x - w / 2, 4, W - w - 4));
    const age = bubbleAge(b);
    const pop = Math.min(1, (state.clock - b.born) / BUBBLE_POP_MS);
    const y = Math.round(head.y - head.ry - BUBBLE_GAP - h + (1 - pop) * RISE);

    ctx.globalAlpha = age > FADE_FROM ? (1 - age) / (1 - FADE_FROM) : 1;

    // The card. Stroked as well as filled, because a bubble full of dark text on
    // top of a dark sky is a bubble nobody can read.
    ctx.fillStyle = '#0d1220';
    ctx.strokeStyle = '#ffd76a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (hasRR) ctx.roundRect(x + 0.5, y + 0.5, w, h, 5);
    else ctx.rect(x + 0.5, y + 0.5, w, h);
    ctx.fill();
    ctx.stroke();

    // The tail points at the head, but only from within the card's own span —
    // otherwise a head near the edge gets a tail out in mid-air.
    const tailX = clamp(head.x, x + 8, x + w - 8);
    ctx.beginPath();
    ctx.moveTo(tailX - 4, y + h);
    ctx.lineTo(tailX + 4, y + h);
    ctx.lineTo(head.x, y + h + BUBBLE_GAP - 4);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#f5f1df';
    ctx.fillText(b.text, x + w / 2, y + h / 2 + 1);
  }

  ctx.globalAlpha = 1;
  ctx.restore();
}
